import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import { MoreHorizontal, Eye, CheckCircle, XCircle } from 'lucide-react'
import { PURCHASE_STATUS_OPTIONS } from '@/modules/shop/constants'
import {
	REWARD_TYPE_LABELS,
	CURRENCY_TYPE_LABELS,
} from '../ShopItemsListing/constants'
import type { components } from '@/schema'

type Purchase = components['schemas']['AdminShopPurchaseResponse']

interface PurchasesTableColumnsProps {
	onViewDetails: (id: string) => void
	onApprove: (id: string) => void
	onReject: (id: string) => void
	onViewDetailPage?: (id: string) => void
}

export const createPurchasesTableColumns = ({
	onViewDetails,
	onApprove,
	onReject,
	onViewDetailPage,
}: PurchasesTableColumnsProps): ColumnDef<Purchase>[] => [
	{
		accessorKey: 'player',
		header: 'ผู้เล่น',
		cell: ({ row }) => {
			const purchase = row.original
			return purchase.player?.username || purchase.playerId
		},
	},
	{
		accessorKey: 'item',
		header: 'สินค้า',
		cell: ({ row }) => {
			const purchase = row.original
			return (
				<div>
					<div className="font-medium">{purchase.item?.name || 'N/A'}</div>
					{purchase.item?.description && (
						<div className="line-clamp-1 text-sm text-muted-foreground">
							{purchase.item.description}
						</div>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'rewardType',
		header: 'ประเภท',
		cell: ({ row }) => {
			const purchase = row.original
			const type = purchase.item?.rewardType
			if (!type) return 'N/A'

			const label =
				REWARD_TYPE_LABELS[type as keyof typeof REWARD_TYPE_LABELS] || type
			const variant =
				type === 'CREDIT'
					? 'default'
					: type === 'FREE_SPIN'
						? 'secondary'
						: 'outline'

			return <Badge variant={variant}>{label}</Badge>
		},
	},
	{
		accessorKey: 'totalPrice',
		header: 'ราคา',
		cell: ({ row }) => {
			const purchase = row.original
			const price = purchase.item.price || 'N/A'
			const currency = purchase.item?.currencyType
				? CURRENCY_TYPE_LABELS[
						purchase.item.currencyType as keyof typeof CURRENCY_TYPE_LABELS
					]
				: 'เพชร'
			return `${price} ${currency}`
		},
	},
	{
		accessorKey: 'status',
		header: 'สถานะ',
		cell: ({ row }) => {
			const status = row.getValue('status') as string
			const option = PURCHASE_STATUS_OPTIONS.find((opt) => opt.value === status)
			const label = option?.label || status

			const variant =
				status === 'PENDING'
					? 'secondary'
					: status === 'SUCCESS'
						? 'default'
						: status === 'FAILED'
							? 'destructive'
							: 'secondary'

			return <Badge variant={variant}>{label}</Badge>
		},
	},
	{
		accessorKey: 'amount',
		header: 'จำนวน',
		cell: ({ row }) => {
			const purchase = row.original
			return purchase.amount.toLocaleString()
		},
	},
	{
		accessorKey: 'createdAt',
		header: 'วันที่สั่งซื้อ',
		cell: ({ row }) => {
			const date = row.getValue('createdAt') as string
			return (
				<div>
					<div className="text-sm">
						{new Date(date).toLocaleDateString('th-TH', {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
						})}
					</div>
					<div className="text-xs text-muted-foreground">
						{new Date(date).toLocaleTimeString('th-TH', {
							hour: '2-digit',
							minute: '2-digit',
						})}
					</div>
				</div>
			)
		},
	},
	{
		accessorKey: 'adminNotes',
		header: 'หมายเหตุ',
		cell: ({ row }) => {
			const purchase = row.original
			if (!purchase.adminNotes) return '-'

			return (
				<div className="max-w-32">
					<div className="truncate text-sm" title={purchase.adminNotes}>
						{purchase.adminNotes}
					</div>
				</div>
			)
		},
	},
	{
		id: 'actions',
		header: 'จัดการ',
		cell: ({ row }) => {
			const purchase = row.original
			const isPending = purchase.status === 'PENDING'

			return (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" size="sm">
							<MoreHorizontal className="h-4 w-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => onViewDetails(purchase.id)}>
							<Eye className="mr-2 h-4 w-4" />
							ดูรายละเอียด (แบบย่อ)
						</DropdownMenuItem>
						{onViewDetailPage && (
							<DropdownMenuItem onClick={() => onViewDetailPage(purchase.id)}>
								<Eye className="mr-2 h-4 w-4" />
								ดูรายละเอียดแบบเต็ม
							</DropdownMenuItem>
						)}
						{isPending && (
							<>
								<DropdownMenuItem
									onClick={() => onApprove(purchase.id)}
									className="text-green-600"
								>
									<CheckCircle className="mr-2 h-4 w-4" />
									อนุมัติ
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => onReject(purchase.id)}
									className="text-red-600"
								>
									<XCircle className="mr-2 h-4 w-4" />
									ปฏิเสธ
								</DropdownMenuItem>
							</>
						)}
					</DropdownMenuContent>
				</DropdownMenu>
			)
		},
	},
]

export const STATUS_FILTER_OPTIONS = PURCHASE_STATUS_OPTIONS

export const REWARD_TYPE_FILTER_OPTIONS = [
	{ label: 'เครดิต', value: 'CREDIT' },
	{ label: 'ฟรีสปิน', value: 'FREE_SPIN' },
	{ label: 'สินค้า', value: 'ITEM' },
]

export const CURRENCY_TYPE_FILTER_OPTIONS = [
	{ label: 'เพชร', value: 'DIAMOND' },
	{ label: 'เครดิต', value: 'CREDIT' },
]
